// Which console this is: the venue and organisation console (`app.{domain}`, spec/13), the HQ console
// (`hq.{domain}`, spec/14) or the public demo (`demo.{domain}`, spec/24). One codebase, three builds — the build
// picks its surface through `clavian:surface` (vite/surface.ts), so the organisation build never contains the
// others.
//
// A surface is its routes and the copy only it speaks. Everything else — the API client, the query
// cache, sign-in, the second factor, the settings renderer, the component kit — is the same code in
// all of them. The demo surface adds three things the others leave out (spec/24 §4.2): its own API client
// (`createApi`, the simulated venue), a frame around the router (`Frame`, the demo window's gate and banner),
// and no realtime socket (`realtime: false`).

import type { ApiClient } from '@clavian/api-client';
import type { CreateI18nOptions } from '@clavian/i18n';
import type { AnyRoute } from '@tanstack/react-router';
import { createContext, useContext, type ComponentType, type ReactNode } from 'react';

export interface ConsoleSurface {
  readonly id: 'org' | 'hq' | 'demo';
  /** On the document as `data-surface`. The HQ build's is also how a test finds HQ in a bundle. */
  readonly name: string;
  /** A fresh route tree: the shared sign-in and signed-in routes, and the surface's own. */
  readonly createRouteTree: () => AnyRoute;
  /** The namespaces this surface's copy is in (`@clavian/i18n/console`, and HQ's `@clavian/i18n/hq`). */
  readonly resources: NonNullable<CreateI18nOptions['extraResources']>;
  /** The API client the browser build uses; absent, the console's own (`createConsoleApi`). */
  readonly createApi?: () => ApiClient;
  /** Wraps the router, inside every provider: the demo's gate and banner. Absent, nothing. */
  readonly Frame?: ComponentType<{ readonly children: ReactNode }>;
  /**
   * Where to go once "Complete your profile" is saved, as a full page load, given the page the visitor was headed
   * to. Absent, the router navigates there. The demo uses it to send a visitor on to the kiosk or both-panes page they
   * chose before signing in, and so the demo window starts only after the profile.
   */
  readonly afterProfileComplete?: (landing: string) => string;
  /** `false` opens no realtime socket: the screens poll as they do when one is down. Absent, a socket. */
  readonly realtime?: false;
}

const SurfaceContext = createContext<ConsoleSurface | null>(null);

export function SurfaceProvider({ surface, children }: { readonly surface: ConsoleSurface; readonly children: ReactNode }) {
  return <SurfaceContext.Provider value={surface}>{children}</SurfaceContext.Provider>;
}

export function useSurface(): ConsoleSurface {
  const surface = useContext(SurfaceContext);
  if (surface === null) throw new Error('useSurface() needs a <SurfaceProvider> above it.');
  return surface;
}

/** The surface, or `null` outside a provider — for a component a test renders on its own. */
export function useOptionalSurface(): ConsoleSurface | null {
  return useContext(SurfaceContext);
}
